import { useAilaStore } from "@features/session/hooks/useAilaStore";
import { useAppStore } from "@hooks/useAppStore";
import { isSafeUrl } from "@memorang/helpers/common";
import { isWeb } from "@memorang/helpers/platform";
import type { UnifiedCitation } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { References } from "@memorang/ui/components/ChatUI/References";
import CitationSourceDialog from "@memorang/ui/components/SessionConfig/CitationSourceDialog";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import { useRouter } from "expo-router";
import { useState } from "react";
import { Linking } from "react-native";

type Props = {
	citations: UnifiedCitation[];
	useWebColors?: boolean;
};

export const Citations = ({ citations, useWebColors = isWeb }: Props) => {
	const router = useRouter();
	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);
	const showTextBasedCitationOnly = useAppStore(
		(state) => state.app.citations?.showTextBasedCitationOnly,
	);

	const [citationsPageNumber, setCitationsPageNumber] = useState<number | null>(
		null,
	);

	const theme = useAppTheme();
	const backgroundColor = useWebColors
		? theme.colors.primaryContainer
		: theme.colors.surface;

	const openUrl = (url: string) => {
		if (!isSafeUrl(url)) {
			return;
		}
		if (isWeb) {
			window.open(url, "_blank", "noopener,noreferrer");
		} else {
			Linking.openURL(url);
		}
	};

	const handleOnPressReference = (reference: UnifiedCitation) => {
		if (reference.url) {
			openUrl(reference.url);
			return;
		}
		if (
			reference.page != null &&
			Number.isInteger(reference.page) &&
			reference.page > 0
		) {
			if (isWeb) {
				setCitationsPageNumber(reference.page);
				return;
			}
			updateShowDrawer(true);
			router.push(`/citation/${reference.page}`);
		}
	};

	return (
		<Box gap={8}>
			<CustomListSubheaderContainer
				enableGutters={false}
				textVariant="titleMedium"
			>
				References
			</CustomListSubheaderContainer>
			<References
				withAccordion={false}
				onPressReference={handleOnPressReference}
				references={citations}
				referenceStyle={{
					backgroundColor,
				}}
				showTextBasedCitationOnly={showTextBasedCitationOnly}
			/>
			<CitationSourceDialog
				pageNumber={citationsPageNumber}
				visible={citationsPageNumber !== null}
				handleClose={() => setCitationsPageNumber(null)}
			/>
		</Box>
	);
};
